import { useMemo } from 'react';
import { Colors } from '../../../../../../themes/colors';
import { EditableField } from '../../../../../commonComponents/EditableField';
import { MultiCheckboxGroup } from '../../../../../commonComponents/MultiCheckboxGroup';
import {
  benefitsOptions,
  compensationOptions,
  CompensationType,
  getPayTypeOptions,
  JobStatus,
} from '../../../shared/constants';
import { useFeatureFlag } from '../../../../../../hooks/useFeatureFlag';
import { FeatureFlagKeys } from '../../../../../../constants/ff.posthog';
import { FormCard } from '../../../shared/FormCard';
import { InfoRow } from '../../../shared/InfoRow';
import { getPayRateSuffix } from '../../../shared/utils';

export const CompensationBenefitsCard = ({
  formValues,
  isEditing,
  job,
  handleChange,
  onCancelClick,
  onEditClick,
  onSaveClick,
}) => {
  const isDailyEnabled = useFeatureFlag(FeatureFlagKeys.addDailyPayOption);
  const payTypeOptionsList = getPayTypeOptions(isDailyEnabled);

  const isFormValid = useMemo(() => {
    const isInvalidMinRate = !formValues.minRate || formValues.minRate <= 0;
    const isInvalidMaxRate =
      (formValues.compensationType === CompensationType.RANGE && !formValues.maxRate) ||
      (formValues.minRate && parseInt(formValues.maxRate, 10) < parseInt(formValues.minRate, 10));

    return (
      !!formValues.payType &&
      !!formValues.compensationType &&
      !isInvalidMaxRate &&
      !isInvalidMinRate
    );
  }, [formValues.compensationType, formValues.maxRate, formValues.minRate, formValues.payType]);

  return (
    <FormCard title="Compensation and Benefits" style={{ marginTop: '16px' }}>
      <div>
        <InfoRow>
          <span className="semi-bold">
            Pay Type{isEditing && <span style={{ color: Colors.error_500 }}>*</span>}:
          </span>
          <EditableField
            editing={isEditing}
            view={<span>{job?.salaryRange?.payType || '-'}</span>}
            edit={
              <select
                className="custom-select custom-select-lg"
                style={{ height: 32, width: 125 }}
                value={formValues.payType}
                onChange={(val) => handleChange('payType', val.target.value)}
              >
                {payTypeOptionsList.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name}
                  </option>
                ))}
              </select>
            }
          />
        </InfoRow>

        {isEditing && (
          <InfoRow>
            <span className="semi-bold">
              Compensation Type<span style={{ color: Colors.error_500 }}>*</span>:
            </span>
            <select
              className="custom-select custom-select-lg"
              style={{ height: 32, width: 200 }}
              value={formValues.compensationType}
              onChange={(val) => handleChange('compensationType', val.target.value)}
            >
              {compensationOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          </InfoRow>
        )}

        <InfoRow>
          <span className="semi-bold">
            {formValues.compensationType === CompensationType.RANGE ? 'Pay Range' : 'Stated Pay'}
            {isEditing && <span style={{ color: Colors.error_500 }}>*</span>}:
          </span>
          <EditableField
            editing={isEditing}
            view={
              <span>
                {`$${job?.salaryRange?.min || '-'}`}
                {!!job?.salaryRange?.max && <span>{` - $${job?.salaryRange?.max || '-'}`}</span>}
              </span>
            }
            edit={
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  className="form-control"
                  style={{ width: 100 }}
                  type="number"
                  placeholder="$"
                  value={formValues.minRate || ''}
                  onChange={(e) => handleChange('minRate', e.target.value.replace(/[^0-9]/g, ''))}
                />
                {formValues.compensationType === CompensationType.RANGE && (
                  <>
                    <span style={{ alignSelf: 'center' }}> to </span>
                    <input
                      className="form-control"
                      style={{ width: 100 }}
                      type="number"
                      placeholder="$"
                      value={formValues.maxRate || ''}
                      onChange={(e) =>
                        handleChange('maxRate', e.target.value.replace(/[^0-9]/g, ''))
                      }
                    />
                  </>
                )}
                <span style={{ alignSelf: 'center', marginLeft: -5 }}>
                  {getPayRateSuffix(formValues.payType)}
                </span>
              </div>
            }
          />
        </InfoRow>

        <InfoRow>
          <span className="semi-bold">Benefits:</span>
          <EditableField
            editing={isEditing}
            view={
              <span style={{ maxWidth: '80%', textAlign: 'right' }}>
                {job?.benefits?.length ? job.benefits.join(', ') : '-'}
              </span>
            }
            edit={
              <div style={{ maxWidth: '80%' }}>
                <MultiCheckboxGroup
                  style={{ justifyContent: 'flex-end' }}
                  options={benefitsOptions.map((option) => ({
                    label: option.name,
                    value: option.code,
                  }))}
                  selectedValues={formValues.benefits || []}
                  onChange={(updated) => handleChange('benefits', updated)}
                />
              </div>
            }
          />
        </InfoRow>

        {formValues.benefits.includes('Other') && (
          <InfoRow>
            <span className="semi-bold">Other Benefits:</span>
            <EditableField
              editing={isEditing}
              view={
                <span style={{ maxWidth: '80%', textAlign: 'right' }}>{job?.benefitsOther}</span>
              }
              edit={
                formValues.benefits.includes('Other') && (
                  <textarea
                    className="form-control"
                    style={{ width: 320 }}
                    id="postTextArea"
                    maxLength={100}
                    placeholder="Enter other benefits here"
                    rows="3"
                    value={formValues.benefitsOther}
                    onChange={(e) => handleChange('benefitsOther', e.target.value)}
                  />
                )
              }
            />
          </InfoRow>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
        {isEditing ? (
          <div style={{ display: 'flex', alignSelf: 'center', gap: 10 }}>
            <button
              type="button"
              className="btn btn-danger"
              style={{ width: 100 }}
              onClick={onCancelClick}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-info"
              style={{ width: 100 }}
              onClick={onSaveClick}
              disabled={!isFormValid}
            >
              Save
            </button>
          </div>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {![JobStatus.CLOSED, JobStatus.SUSPENDED].includes(job?.status) && (
              <button
                type="button"
                className="btn btn-info"
                style={{ width: 150, alignSelf: 'center' }}
                onClick={onEditClick}
              >
                Edit
              </button>
            )}
          </>
        )}
      </div>
    </FormCard>
  );
};
